import React, { useEffect, useState } from 'react';
import { Table, TableBody, TableRow, TableCell, Grid } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Close, Check } from '@mui/icons-material';
import LoaderComponent from '../../../Utilities/LoaderComponent';
import { convertToFormattedNumber } from '../../../TableContainer/TableUtilities/columnFormatter';
import { colors } from '../../../../Utilities/LenoxColors';
import { NetworkRequest } from '../../../Utilities/NetworkRequests/NetworkRequests';

const useStyles = makeStyles()(() => ({
  tableCell: {
    width: '50%'
  },
  tableCellMulti: {
    textAlign: 'right'
  },
  tableCellFormConv: {
    paddingLeft: '5px',
    paddingRight: '10px',
    textAlign: 'right'
  },
  tableCellMultiDisplay: {
    textAlign: 'left'
  },
  tableCellHead: {
    fontWeight: 'bold',
    textAlign: 'center',
    paddingLeft: '5px',
    paddingRight: '10px'
  },
  tableContainer: {
    border: 'solid 1px rgba(20,20,20,.3)',
    borderRadius: '8px',
    margin: '1rem'
  },
  table: {},
  tableTitle: {
    fontSize: '1.25rem',
    textAlign: 'center',
    padding: '.5rem',
    backgroundColor: colors.lenoxDark2,
    color: colors.lenoxLight1,
    borderTopLeftRadius: '6px',
    borderTopRightRadius: '6px'
  },
  tableDescription: {
    fontSize: '.75rem',
    padding: '.5rem',
    borderBottom: 'solid 1px rgba(20,20,20,.3)'
  },
  greenCheck: { color: 'green' },
  redCross: {
    color: 'red'
  }
}));

const renderCheckOrCross = (val, classes) =>
  val && val !== '0' ? (
    <Check className={classes.greenCheck} />
  ) : (
    <Close className={classes.redCross} />
  );

const payoutsMapping = [
  {
    value: 'phone_payout_set',
    display: 'Phone Payout Set',
    formatter: (value, classes) => renderCheckOrCross(value, classes)
  },
  {
    value: 'auto_flat_rate',
    display: 'Auto Flat Rate',
    formatter: convertToFormattedNumber
  },
  {
    value: 'home_flat_rate',
    display: 'Home Flat Rate',
    formatter: convertToFormattedNumber
  },
  {
    value: 'life_flat_rate',
    display: 'Life Flat Rate',
    formatter: convertToFormattedNumber
  },
  {
    value: 'fe_flat_rate',
    display: 'Final Expense Flat Rate',
    formatter: convertToFormattedNumber
  },
  {
    value: 'pet_flat_rate',
    display: 'Pet Flat Rate',
    formatter: convertToFormattedNumber
  },
  {
    value: 'commercial_flat_rate',
    display: 'Commercial Flat Rate',
    formatter: convertToFormattedNumber
  },
  {
    value: 'auto_revshare',
    display: 'Auto Revshare',
    formatter: convertToFormattedNumber
  },
  {
    value: 'home_revshare',
    display: 'Home Revshare',
    formatter: convertToFormattedNumber
  },
  {
    value: 'life_revshare',
    display: 'Life Revshare',
    formatter: convertToFormattedNumber
  },
  {
    value: 'fe_revshare',
    display: 'Final Expense Revshare',
    formatter: convertToFormattedNumber
  },
  {
    value: 'pet_revshare',
    display: 'Pet Revshare',
    formatter: convertToFormattedNumber
  },
  {
    value: 'commercial_revshare',
    display: 'Commercial Revshare',
    formatter: convertToFormattedNumber
  }
];

const pixelMapping = [
  {
    value: 'one_pixel',
    display: 'TY Pixel Set',
    formatter: (value, classes) => renderCheckOrCross(value, classes)
  }
];

const linksMapping = [
  {
    value: 'auto_automated_testing',
    display: 'Auto - Automated Testing',
    formatter: (value, classes) => renderCheckOrCross(value, classes)
  },
  {
    value: 'auto_weighted',
    display: 'Auto - Weighted Is On',
    formatter: (value, classes) => renderCheckOrCross(value, classes)
  },
  {
    value: 'home_automated_testing',
    display: 'Home - Automated Testing',
    formatter: (value, classes) => renderCheckOrCross(value, classes)
  },
  {
    value: 'home_weighted',
    display: 'Home - Weighted Is On',
    formatter: (value, classes) => renderCheckOrCross(value, classes)
  },
  {
    value: 'life_automated_testing',
    display: 'Life - Automated Testing',
    formatter: (value, classes) => renderCheckOrCross(value, classes)
  },
  {
    value: 'life_weighted',
    display: 'Life - Weighted Is On',
    formatter: (value, classes) => renderCheckOrCross(value, classes)
  },
  {
    value: 'fe_automated_testing',
    display: 'Final Expense - Automated Testing',
    formatter: (value, classes) => renderCheckOrCross(value, classes)
  },
  {
    value: 'fe_weighted',
    display: 'Final Expense - Weighted Is On',
    formatter: (value, classes) => renderCheckOrCross(value, classes)
  },
  {
    value: 'pet_automated_testing',
    display: 'Pet - Automated Testing',
    formatter: (value, classes) => renderCheckOrCross(value, classes)
  },
  {
    value: 'pet_weighted',
    display: 'Pet - Weighted Is On',
    formatter: (value, classes) => renderCheckOrCross(value, classes)
  },
  {
    value: 'commercial_automated_testing',
    display: 'Commercial - Automated Testing',
    formatter: (value, classes) => renderCheckOrCross(value, classes)
  },
  {
    value: 'commercial_weighted',
    display: 'Commercial - Weighted Is On',
    formatter: (value, classes) => renderCheckOrCross(value, classes)
  }
];

const sourcesMapping = [
  {
    value: 'auto_everquote',
    display: 'Auto - Everquote Source',
    formatter: (value, classes) => renderCheckOrCross(value, classes)
  },
  {
    value: 'auto_quinstreet',
    display: 'Auto - Quinstreet Source',
    formatter: (value, classes) => renderCheckOrCross(value, classes)
  },
  {
    value: 'auto_transparently',
    display: 'Auto - Transparently Source',
    formatter: (value, classes) => renderCheckOrCross(value, classes)
  },
  {
    value: 'auto_mediaalpha',
    display: 'Auto - Media Alpha Source',
    formatter: (value, classes) => renderCheckOrCross(value, classes)
  },
  {
    value: 'home_everquote',
    display: 'Home - Everquote Source',
    formatter: (value, classes) => renderCheckOrCross(value, classes)
  },
  {
    value: 'home_quinstreet',
    display: 'Home - Quinstreet Source',
    formatter: (value, classes) => renderCheckOrCross(value, classes)
  },
  {
    value: 'home_transparently',
    display: 'Home - Transparently Source',
    formatter: (value, classes) => renderCheckOrCross(value, classes)
  },
  {
    value: 'home_mediaalpha',
    display: 'Home - Media Alpha Source',
    formatter: (value, classes) => renderCheckOrCross(value, classes)
  },
  {
    value: 'life_quinstreet',
    display: 'Life - Quinstreet Source',
    formatter: (value, classes) => renderCheckOrCross(value, classes)
  },
  {
    value: 'life_mediaalpha',
    display: 'Life - Media Alpha Source',
    formatter: (value, classes) => renderCheckOrCross(value, classes)
  },
  {
    value: 'fe_quinstreet',
    display: 'Final Expense - Quinstreet Source',
    formatter: (value, classes) => renderCheckOrCross(value, classes)
  },
  {
    value: 'fe_mediaalpha',
    display: 'Final Expense - Media Alpha Source',
    formatter: (value, classes) => renderCheckOrCross(value, classes)
  },
  {
    value: 'commercial_quinstreet',
    display: 'Commercial - Quinstreet Source',
    formatter: (value, classes) => renderCheckOrCross(value, classes)
  },
  {
    value: 'pet_mediaalpha',
    display: 'Pet - Media Alpha Source',
    formatter: (value, classes) => renderCheckOrCross(value, classes)
  }
];

const miscMapping = [
  {
    value: 'phone_number_set_auto',
    display: 'Phone Number Set - Auto',
    formatter: (value, classes) => renderCheckOrCross(value, classes)
  },
  {
    value: 'phone_number_set_home',
    display: 'Phone Number Set - Home',
    formatter: (value, classes) => renderCheckOrCross(value, classes)
  },
  {
    value: 'phone_number_set_life',
    display: 'Phone Number Set - Life',
    formatter: (value, classes) => renderCheckOrCross(value, classes)
  },
  {
    value: 'phone_number_set_fe',
    display: 'Phone Number Set - Final Expense',
    formatter: (value, classes) => renderCheckOrCross(value, classes)
  },
  {
    value: 'phone_number_set_pet',
    display: 'Phone Number Set - Pet',
    formatter: (value, classes) => renderCheckOrCross(value, classes)
  },
  {
    value: 'phone_number_set_commercial',
    display: 'Phone Number Set - Commercial',
    formatter: (value, classes) => renderCheckOrCross(value, classes)
  },
  {
    value: 'phone_number_set_health',
    display: 'Phone Number Set - Health',
    formatter: (value, classes) => renderCheckOrCross(value, classes)
  },
  {
    value: 'phone_number_set_medicare',
    display: 'Phone Number Set - Medicare',
    formatter: (value, classes) => renderCheckOrCross(value, classes)
  }
];

const AffiliateSetupCheck = function ({ row }) {
  const { classes } = useStyles();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({});

  const getAffiliateSetupCheck = async (affiliate_id) => {
    const { data: response } = await NetworkRequest('affiliates', {
      action: 'getAffiliateSetupCheck',
      affiliate_id
    });
    return response;
  };

  useEffect(() => {
    getAffiliateSetupCheck(row.affiliate_id)
      .then((response) => setData(response))
      .then(() => setLoading(false));
    // eslint-disable-next-line
  }, []);

  const renderTableCell = (cellData, className = 'tableCell') => (
    <TableCell className={classes[className]}>{cellData}</TableCell>
  );

  const renderRow = (rowData, section) => (
    <TableRow>
      {renderTableCell(rowData.display)}
      {renderTableCell(
        data && data[section] && data[section][rowData.value]
          ? rowData.formatter(data[section][rowData.value], classes)
          : renderCheckOrCross(false, classes)
      )}
    </TableRow>
  );

  const renderTable = (title, dataMapping = []) => (
    <Grid item xs={10} className={classes.tableContainer}>
      <div className={classes.tableTitle}>{title}</div>
      <Table className={classes.table}>
        <TableBody>
          {dataMapping.map((dataRow) => {
            return renderRow(dataRow, title.toLowerCase());
          })}
        </TableBody>
      </Table>
    </Grid>
  );

  return (
    <Grid
      container
      justifyContent="center"
      alignItems="center"
      style={{ marginBottom: '5rem' }}
    >
      {loading ? (
        <LoaderComponent padding={150} />
      ) : (
        <>
          {renderTable('Payouts', payoutsMapping)}
          {renderTable('Pixel', pixelMapping)}
          {renderTable('Links', linksMapping)}
          {renderTable('Sources', sourcesMapping)}
          {renderTable('Misc', miscMapping)}
        </>
      )}
    </Grid>
  );
};

export default AffiliateSetupCheck;
